import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { reconcileLedger, type ReconcileResult } from "@/lib/financialLedger";

const LABELS: Record<string, string> = {
  missing_ledger_entries: "Missing ledger entries",
  duplicate_transactions: "Duplicate transactions",
  unlinked_payments: "Unlinked payments",
  paid_payroll_without_expense: "Paid payroll with no expense",
  ledger_payroll_without_record: "Ledger payroll with no payroll record",
  refunds_without_original: "Refunds without an original payment",
  amount_mismatches: "Amount mismatches",
  date_mismatches: "Date mismatches",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  startDate: string;
  endDate: string;
  onDone: () => void;
}

export function ReconcileDialog({ open, onOpenChange, startDate, endDate, onDone }: Props) {
  const { toast } = useToast();
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setResult(null); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await reconcileLedger({ start_date: startDate, end_date: endDate, persist: true });
        setResult(r);
        onDone();
      } catch (e) {
        toast({ title: "Reconciliation failed", description: (e as Error).message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startDate, endDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reconciliation</DialogTitle>
          <DialogDescription>
            Compares registration payments, refunds, payroll, Grace payments, and manual entries against the ledger
            for {startDate} – {endDate}.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking every record…
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {result.discrepancy_count === 0 ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Everything reconciles</AlertTitle>
                <AlertDescription>Reconciled through {result.reconciled_through}.</AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{result.discrepancy_count} unresolved discrepanc{result.discrepancy_count === 1 ? "y" : "ies"}</AlertTitle>
                <AlertDescription>
                  This period is not marked reconciled until every item below is resolved.
                </AlertDescription>
              </Alert>
            )}

            {Object.entries(result.findings).map(([key, rows]) => (
              <div key={key} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{LABELS[key] ?? key}</p>
                  <Badge variant={rows.length ? "destructive" : "secondary"}>{rows.length}</Badge>
                </div>
                {rows.length > 0 && (
                  <ul className="mt-2 text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                    {rows.map((r: any, i: number) => (
                      <li key={i}>{JSON.stringify(r)}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
